/**
 * TenantLifecycleTab — Pannello gestione stato lifecycle del tenant (super_admin).
 *
 * Sezioni:
 *  1. Stato corrente: chip colorato + scadenza grace period + info motivazioni
 *  2. Cambio manuale: dropdown con i 5 stati validi + note + bottone applica
 *  3. Storico transizioni: ultime 20 entry da tenant.status_history (rolling)
 *
 * Stile editoriale console: Fraunces title, mono per ID/event, chip semantici.
 *
 * @since 2026-05-20 (industry-standard tenant lifecycle)
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, TextField, MenuItem,
  Alert, Snackbar, Divider, Tooltip, IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { tokens } from '../theme';

type TenantStatus = 'active' | 'grace_period' | 'restricted' | 'suspended' | 'canceled' | 'deleted';

interface StatusHistoryEntry {
  previous_status: string;
  new_status: string;
  reason: string;
  actor_uid?: string;
  stripe_event_id?: string;
  notes?: string | null;
  at: string;
}

interface TenantLite {
  id: string;
  nome_azienda: string;
  tenant_status?: TenantStatus;
  grace_period_ends_at?: { seconds: number } | string;
  status_changed_at?: { seconds: number } | string;
  status_history?: StatusHistoryEntry[];
}

interface Props {
  tenant: TenantLite;
  onChange: () => void;
}

const STATUS_LABEL: Record<TenantStatus, string> = {
  active: 'Attivo',
  grace_period: 'Periodo di grazia',
  restricted: 'Accesso limitato',
  suspended: 'Sospeso',
  canceled: 'Cancellato',
  deleted: 'Eliminato',
};

const STATUS_COLOR_TOKEN: Record<TenantStatus, string> = {
  active: tokens.color.success,
  grace_period: tokens.color.warning,
  restricted: tokens.color.danger,
  suspended: tokens.color.ash,
  canceled: tokens.color.mist,
  deleted: tokens.color.graphite,
};

const STATUS_DESCRIPTION: Record<TenantStatus, string> = {
  active: 'Accesso completo, default. Tenant operativo a regime.',
  grace_period: 'Pagamento fallito. Banner top giallo per l\'utente, accesso pieno. Cron daily porta a "restricted" al termine del periodo di grazia.',
  restricted: 'Capability limitate alle sole settings + billing + marketplace. Cron daily porta a "suspended" dopo 30 giorni.',
  suspended: 'Login bloccato con lockout screen. Dati intatti. Cron daily porta a "canceled" dopo 60 giorni.',
  canceled: 'Subscription cancellata. Modalità "ricovero" 90gg per export dati o riattivazione.',
  deleted: 'Hard delete completato. Stato irreversibile.',
};

const REASON_LABEL: Record<string, string> = {
  stripe_webhook: 'Stripe',
  admin_action: 'Admin manuale',
  cron_grace_expired: 'Cron · grace period scaduto',
  cron_restricted_to_suspended: 'Cron · da restricted',
  cron_suspended_to_canceled: 'Cron · da suspended',
  hard_delete: 'Hard delete',
  reactivation: 'Riattivazione',
  blocco_accesso: 'Blocco dell\'accesso',
  sblocco_accesso: 'Sblocco dell\'accesso',
};

function formatTimestamp(value: { seconds: number } | string | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : new Date(value.seconds * 1000);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
}

function daysUntil(value: { seconds: number } | string | undefined): number | null {
  if (!value) return null;
  const date = typeof value === 'string' ? new Date(value) : new Date(value.seconds * 1000);
  if (isNaN(date.getTime())) return null;
  return Math.ceil((date.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

export const TenantLifecycleTab: React.FC<Props> = ({ tenant, onChange }) => {
  const currentStatus = (tenant.tenant_status || 'active') as TenantStatus;
  const [targetStatus, setTargetStatus] = useState<TenantStatus | ''>('');
  const [notes, setNotes] = useState('');
  const [gracePeriodDays, setGracePeriodDays] = useState<number | ''>(14);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const history = useMemo(() => {
    const arr = tenant.status_history || [];
    return [...arr].reverse().slice(0, 20);
  }, [tenant.status_history]);

  const graceDaysLeft = useMemo(() => daysUntil(tenant.grace_period_ends_at), [tenant.grace_period_ends_at]);

  const handleApplyStatusChange = async () => {
    if (!targetStatus) return;
    setSubmitting(true);
    try {
      const fn = httpsCallable(functions, 'changeTenantStatus');
      const payload: any = {
        tenant_id: tenant.id,
        new_status: targetStatus,
        notes: notes.trim() || undefined,
      };
      if (targetStatus === 'grace_period' && gracePeriodDays && Number(gracePeriodDays) > 0) {
        payload.grace_period_days_override = Number(gracePeriodDays);
      }
      const result = await fn(payload);
      const data = result.data as { previous_status?: string; new_status?: string; claims_users_updated?: number };
      setSnack({
        msg: `Stato cambiato: ${data.previous_status} → ${data.new_status} (${data.claims_users_updated} utenti sincronizzati)`,
        severity: 'success',
      });
      setTargetStatus('');
      setNotes('');
      onChange();
    } catch (e: any) {
      setSnack({ msg: e?.message || 'Errore cambio stato', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const validTransitions: TenantStatus[] = useMemo(() => {
    return (['active', 'grace_period', 'restricted', 'suspended', 'canceled'] as TenantStatus[])
      .filter((s) => s !== currentStatus);
  }, [currentStatus]);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: tokens.font.display,
            fontWeight: 600,
            letterSpacing: '-0.02em',
          }}
        >
          Lifecycle tenant
        </Typography>
        <Tooltip title="Ricarica">
          <IconButton size="small" onClick={onChange}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>

      {/* ====== STATO CORRENTE ====== */}
      <Box mb={4}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Stato corrente
        </Typography>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <Chip
            label={STATUS_LABEL[currentStatus]}
            sx={{
              bgcolor: STATUS_COLOR_TOKEN[currentStatus],
              color: '#fff',
              fontWeight: 600,
              px: 1,
              fontFamily: tokens.font.body,
            }}
          />
          {currentStatus === 'grace_period' && graceDaysLeft != null && (
            <Typography variant="body2" color="text.secondary">
              Scadenza tra <strong>{graceDaysLeft} {graceDaysLeft === 1 ? 'giorno' : 'giorni'}</strong> ({formatTimestamp(tenant.grace_period_ends_at)})
            </Typography>
          )}
          {tenant.status_changed_at && (
            <Typography variant="caption" color="text.secondary">
              Ultimo cambio: {formatTimestamp(tenant.status_changed_at)}
            </Typography>
          )}
        </Stack>

        <Alert severity="info" icon={<InfoIcon />} sx={{ '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
          {STATUS_DESCRIPTION[currentStatus]}
        </Alert>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* ====== CAMBIO MANUALE ====== */}
      <Box mb={4}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Cambio stato manuale
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }} mt={1.5}>
          <TextField
            select
            label="Nuovo stato"
            value={targetStatus}
            onChange={(e) => setTargetStatus(e.target.value as TenantStatus)}
            size="small"
            sx={{ minWidth: 240 }}
            disabled={submitting}
          >
            <MenuItem value="" disabled>Seleziona...</MenuItem>
            {validTransitions.map((s) => (
              <MenuItem key={s} value={s}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLOR_TOKEN[s] }} />
                  <Typography variant="body2">{STATUS_LABEL[s]}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          {targetStatus === 'grace_period' && (
            <TextField
              label="Giorni di grazia"
              type="number"
              value={gracePeriodDays}
              onChange={(e) => setGracePeriodDays(e.target.value ? parseInt(e.target.value, 10) : '')}
              size="small"
              sx={{ width: 140 }}
              disabled={submitting}
              inputProps={{ min: 1, max: 90 }}
              helperText="Default: 14gg"
            />
          )}

          <TextField
            label="Note (motivazione)"
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 300))}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            disabled={submitting}
            placeholder="es. Pagamento offline ricevuto, riattivazione manuale"
          />

          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowForwardIcon />}
            onClick={handleApplyStatusChange}
            disabled={!targetStatus || submitting}
            sx={{ minWidth: 140, textTransform: 'none', fontWeight: 600 }}
          >
            {submitting ? 'Applico...' : 'Applica'}
          </Button>
        </Stack>

        {targetStatus && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Effetti del cambio:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
              {STATUS_DESCRIPTION[targetStatus]}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Il custom claim verrà sincronizzato sugli utenti del tenant. Le modifiche saranno visibili dopo refresh del token (entro 1 ora) o nuovo login.
            </Typography>
          </Alert>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* ====== STORICO ====== */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Storico transizioni ({history.length}{tenant.status_history && tenant.status_history.length > 20 ? ' di ' + tenant.status_history.length : ''})
        </Typography>

        {history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Nessuna transizione registrata. Lo stato del tenant è quello di default.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {history.map((entry, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1.25,
                  bgcolor: tokens.color.lineSoft,
                  borderColor: tokens.color.line,
                  borderRadius: 1,
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} flexWrap="wrap">
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Chip
                      label={STATUS_LABEL[entry.previous_status as TenantStatus] || entry.previous_status}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                    <ArrowForwardIcon sx={{ fontSize: 14, color: tokens.color.mist }} />
                    <Chip
                      label={STATUS_LABEL[entry.new_status as TenantStatus] || entry.new_status}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        height: 22,
                        bgcolor: STATUS_COLOR_TOKEN[entry.new_status as TenantStatus] || tokens.color.mist,
                        color: '#fff',
                      }}
                    />
                  </Stack>

                  <Typography variant="caption" sx={{ fontFamily: tokens.font.mono, fontSize: '0.7rem' }}>
                    {REASON_LABEL[entry.reason] || entry.reason}
                  </Typography>

                  {entry.notes && (
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      {entry.notes}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: tokens.font.mono, fontSize: '0.7rem' }}>
                    {formatTimestamp(entry.at as any)}
                  </Typography>
                </Stack>

                {(entry.actor_uid || entry.stripe_event_id) && (
                  <Stack direction="row" spacing={1.5} mt={0.5} sx={{ pl: 0.5 }}>
                    {entry.actor_uid && (
                      <Typography variant="caption" sx={{ fontFamily: tokens.font.mono, fontSize: '0.65rem', color: tokens.color.mist }}>
                        actor: {entry.actor_uid.slice(0, 16)}...
                      </Typography>
                    )}
                    {entry.stripe_event_id && (
                      <Typography variant="caption" sx={{ fontFamily: tokens.font.mono, fontSize: '0.65rem', color: tokens.color.mist }}>
                        stripe: {entry.stripe_event_id.slice(0, 20)}...
                      </Typography>
                    )}
                  </Stack>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : <span />}
      </Snackbar>
    </Paper>
  );
};
